'use client';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import type { ElasticRetailer } from '@//app/api/retailers/[slug]';
import { InlineButton } from '@//components/InlineButton';
import { Link } from '@//components/Link';
import { useLegacyTrackMailSentRetailer } from '@//data/tracking/useLegacyTrackMailSentRetailer';
import { useLegacyTrackMailVisibleRetailer } from '@//data/tracking/useLegacyTrackMailVisibleRetailer';
import styles from '../../RetailerPage.module.scss';

interface RetailerEmailProps {
  retailer: ElasticRetailer;
}

const RetailerEmail = ({ retailer }: RetailerEmailProps) => {
  const tCommon = useTranslations('other.common');
  const [isVisible, setIsVisible] = useState(false);
  const { trackLegacyTrackMailSentRetailer } = useLegacyTrackMailSentRetailer();
  const { trackLegacyTrackMailVisibleRetailer } = useLegacyTrackMailVisibleRetailer();

  const onShowClick = useCallback(() => {
    trackLegacyTrackMailVisibleRetailer(retailer);
    setIsVisible(true);
  }, [trackLegacyTrackMailVisibleRetailer, retailer]);

  const onEmailLinkClick = useCallback(
    () => trackLegacyTrackMailSentRetailer(retailer),
    [trackLegacyTrackMailSentRetailer, retailer],
  );

  if (!retailer.email) return null;

  return (
    <>
      <dt className={styles.descriptionListLabel}>
        <abbr title={tCommon('emailAddress.label')}>{tCommon('emailAddress.short')}</abbr>
      </dt>
      <dd className={styles.descriptionListValue}>
        {isVisible ? (
          <Link
            href={`mailto:${retailer.email}`}
            title={tCommon('emailAddress.linkAriaLabel', {
              emailAddress: retailer.email,
            })}
            onClick={onEmailLinkClick}
          >
            {retailer.email}
          </Link>
        ) : (
          <InlineButton
            onClick={onShowClick}
            title={tCommon('emailAddress.showEmailAddress')}
            weight="strong"
          >
            {tCommon('emailAddress.showEmailAddress')}
          </InlineButton>
        )}
      </dd>
    </>
  );
};

export default RetailerEmail;
